import React, { Fragment, useEffect, useState } from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {
    Grid,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel, Checkbox
} from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import strings from 'strings';
import Drawer, { DrawerAction } from 'components/shared/Drawer';
import Button from 'components/shared/Button';
import './index.scss'
import FormCheckBox from "../../../../forms/ReactHookForm/Checkbox/FormCheckBox";
import { useAppContext } from '../../../../../context/AppContext';

const t = strings.APPLICANT.VERIFICATION

const DEMO_SCENARIOS = [
    { value: 'watchlist', label: 'Watchlist hits' },
    { value: 'clear', label: 'All clear' },
    { value: 'deceased', label: 'Deceased owner' },
    { value: 'two_owners', label: 'Two owners' },
    { value: 'no_owner', label: 'No owner' },
]

function VerificationPreview(props) {
    const {
        open,
        onClose,
        verificationData,
        onSubmit,
        loading
    } = props

    const [activeReps, setActiveReps] = useState([])
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [pendingFormData, setPendingFormData] = useState(null)
    const [demoScenario, setDemoScenario] = useState('watchlist')
    const { isDemoMode } = useAppContext()

    const methods = useForm()
    const { register, handleSubmit, formState, reset } = methods
    const { errors } = formState

    const buildVerificationDefaultValues = (verificationData) => {
        const values = {
            companyName: verificationData?.Company?.CompanyName || '',
            alternateCompanyName: verificationData?.Company?.AlternateCompanyName || '',
            companyFein: verificationData?.Company?.FEIN || '',
            companyStreetAddress1: verificationData?.Company?.Address?.StreetAddress1 || '',
            companyStreetAddress2: verificationData?.Company?.Address?.StreetAddress2 || '',
            companyCity: verificationData?.Company?.Address?.City || '',
            companyState: verificationData?.Company?.Address?.State || '',
            companyPostalCode: verificationData?.Company?.Address?.PostalCode || '',
            companyPhone: verificationData?.Company?.Phone || '',
        }

        const formatDOB = (dob) => {
            if (!dob) return ''
            // If it's already a string, return it
            if (typeof dob === 'string') return dob
            // If it's an object with Year/Month/Day, convert to YYYY-MM-DD
            if (dob.Year && dob.Month && dob.Day) {
                const year = String(dob.Year).padStart(4, '0')
                const month = String(dob.Month).padStart(2, '0')
                const day = String(dob.Day).padStart(2, '0')
                return `${year}-${month}-${day}`
            }
            return ''
        }

        for (let i = 1; i <= 5; i++) {
            const rep = verificationData?.[`AuthorizedRep${i}`]
            if (rep) {
                values[`rep${i}FirstName`] = rep?.Name?.First || ''
                values[`rep${i}LastName`] = rep?.Name?.Last || ''
                values[`rep${i}FormerLastName`] = rep?.FormerLastName || ''
                values[`rep${i}StreetAddress1`] = rep?.Address?.StreetAddress1 || ''
                values[`rep${i}StreetAddress2`] = rep?.Address?.StreetAddress2 || ''
                values[`rep${i}City`] = rep?.Address?.City || ''
                values[`rep${i}State`] = rep?.Address?.State || ''
                values[`rep${i}PostalCode`] = rep?.Address?.PostalCode || ''
                values[`rep${i}DOB`] = formatDOB(rep?.DOB)
                values[`rep${i}Age`] = rep?.Age || ''
                values[`rep${i}SSN`] = rep?.SSN || ''
                values[`rep${i}Phone`] = rep?.Phone || ''
                values[`rep${i}DriverLicenseNumber`] = rep?.DriverLicenseNumber || ''
                values[`rep${i}DriverLicenseState`] = rep?.DriverLicenseState || ''
                values[`rep${i}Email`] = rep?.Email || ''
            }
        }

        return values
    }
    const buildVerificationSubmitData = (formData) => {
        const cleanValue = (value) => value || undefined

        const parseDOB = (dobString) => {
            if (!dobString) return undefined

            // Convert to string if it's not already
            const dateStr = typeof dobString === 'string' ? dobString : String(dobString)

            // Handle if it's already an object with Year/Month/Day structure
            if (typeof dobString === 'object' && dobString?.Year) {
                return dobString
            }

            const [year, month, day] = dateStr.split('-')
            return {
                Year: parseInt(year),
                Month: parseInt(month),
                Day: parseInt(day)
            }
        }

        const submitData = {
            requestExactMatch: !!formData.requestExactMatch,
            ...(isDemoMode && { demoScenario }),
            Company: {
                CompanyName: cleanValue(formData.companyName),
                AlternateCompanyName: cleanValue(formData.alternateCompanyName),
                FEIN: cleanValue(formData.companyFein?.replace(/-/g, '')),
                Address: {
                    StreetAddress1: cleanValue(formData.companyStreetAddress1),
                    StreetAddress2: cleanValue(formData.companyStreetAddress2),
                    City: cleanValue(formData.companyCity),
                    State: cleanValue(formData.companyState),
                    PostalCode: cleanValue(formData.companyPostalCode),
                },
                Phone: cleanValue(formData.companyPhone),
            },
        }

        // Clean up empty nested objects
        submitData.Company.Address = Object.fromEntries(
            Object.entries(submitData.Company.Address).filter(([, v]) => v !== undefined)
        )
        submitData.Company = Object.fromEntries(
            Object.entries(submitData.Company).filter(([, v]) => v !== undefined && (typeof v !== 'object' || Object.keys(v).length > 0))
        )

        for (let i = 1; i <= 5; i++) {
            const repFirstName = formData[`rep${i}FirstName`]
            // Only add rep if first name exists AND rep is in activeReps
            if (repFirstName && activeReps.includes(i)) {
                submitData[`AuthorizedRep${i}`] = {
                    Name: {
                        First: cleanValue(formData[`rep${i}FirstName`]),
                        Last: cleanValue(formData[`rep${i}LastName`]),
                    },
                    FormerLastName: cleanValue(formData[`rep${i}FormerLastName`]),
                    Address: {
                        StreetAddress1: cleanValue(formData[`rep${i}StreetAddress1`]),
                        StreetAddress2: cleanValue(formData[`rep${i}StreetAddress2`]),
                        City: cleanValue(formData[`rep${i}City`]),
                        State: cleanValue(formData[`rep${i}State`]),
                        PostalCode: cleanValue(formData[`rep${i}PostalCode`]),
                    },
                    DOB: parseDOB(formData[`rep${i}DOB`]),
                    Age: cleanValue(formData[`rep${i}Age`]),
                    SSN: cleanValue(formData[`rep${i}SSN`]),
                    Phone: cleanValue(formData[`rep${i}Phone`]),
                    DriverLicenseNumber: cleanValue(formData[`rep${i}DriverLicenseNumber`]),
                    DriverLicenseState: cleanValue(formData[`rep${i}DriverLicenseState`]),
                    Email: cleanValue(formData[`rep${i}Email`]),
                }

                // Clean up empty nested objects in rep
                submitData[`AuthorizedRep${i}`].Address = Object.fromEntries(
                    Object.entries(submitData[`AuthorizedRep${i}`].Address).filter(([, v]) => v !== undefined)
                )
                submitData[`AuthorizedRep${i}`].Name = Object.fromEntries(
                    Object.entries(submitData[`AuthorizedRep${i}`].Name).filter(([, v]) => v !== undefined)
                )
                submitData[`AuthorizedRep${i}`] = Object.fromEntries(
                    Object.entries(submitData[`AuthorizedRep${i}`]).filter(([, v]) => v !== undefined && (typeof v !== 'object' || Object.keys(v).length > 0))
                )
            }
        }

        return submitData
    }

    useEffect(() => {
        if (verificationData) {
            const initialReps = []
            for (let i = 1; i <= 5; i++) {
                if (verificationData?.[`AuthorizedRep${i}`]) {
                    initialReps.push(i)
                }
            }
            setActiveReps(initialReps)
            reset(buildVerificationDefaultValues(verificationData))
        }
    }, [verificationData, reset])

    if (!open || !verificationData) {
        return null
    }

    const handleFormSubmit = (data) => {
        setPendingFormData(data)
        setConfirmDialogOpen(true)
    }

    const handleConfirmSubmit = () => {
        setConfirmDialogOpen(false)
        if (pendingFormData) {
            const structuredData = buildVerificationSubmitData(pendingFormData, activeReps)
            onSubmit(structuredData)
            setPendingFormData(null)
        }
    }


    const handleCancelSubmit = () => {
        setConfirmDialogOpen(false)
        setPendingFormData(null)
    }

    const addRepresentative = () => {
        for (let i = 1; i <= 5; i++) {
            if (!activeReps.includes(i)) {
                setActiveReps([...activeReps, i])
                return
            }
        }
    }

    const removeRepresentative = (repIndex) => {
        setActiveReps(activeReps.filter(i => i !== repIndex))
    }

    const renderAuthorizedRepresentative = (repIndex) => {
        const rep = verificationData?.[`AuthorizedRep${repIndex}`]
        const isNewRep = !rep

        return (
            <Fragment key={repIndex}>
                <div className='d-flex align-items-center justify-content-between mt-4 mb-2'>
                    <div className='heading-style-2'>Authorized Representative {repIndex}</div>
                    {activeReps.length > 0 && (
                        <IconButton
                            size='small'
                            onClick={() => removeRepresentative(repIndex)}
                            title='Remove Representative'
                        >
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    )}
                </div>
                <hr className='mb-3' />
                <Grid container spacing={2}>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='First Name'
                            {...register(`rep${repIndex}FirstName`, { required: 'First Name is required' })}
                            error={!!errors[`rep${repIndex}FirstName`]}
                            helperText={errors[`rep${repIndex}FirstName`]?.message}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Last Name'
                            {...register(`rep${repIndex}LastName`, { required: 'Last Name is required' })}
                            error={!!errors[`rep${repIndex}LastName`]?.message}
                            helperText={errors[`rep${repIndex}LastName`]?.message}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Former Last Name'
                            {...register(`rep${repIndex}FormerLastName`)}
                        />
                    </Grid>
                    <Grid item sm={12}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Street Address 1'
                            {...register(`rep${repIndex}StreetAddress1`)}
                        />
                    </Grid>
                    <Grid item sm={12}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Street Address 2'
                            {...register(`rep${repIndex}StreetAddress2`)}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='City'
                            {...register(`rep${repIndex}City`)}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='State'
                            {...register(`rep${repIndex}State`)}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Postal Code'
                            {...register(`rep${repIndex}PostalCode`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Date of Birth'
                            type='date'
                            InputLabelProps={{ shrink: true }}
                            {...register(`rep${repIndex}DOB`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Age'
                            {...register(`rep${repIndex}Age`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='SSN'
                            {...register(`rep${repIndex}SSN`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Phone'
                            {...register(`rep${repIndex}Phone`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Driver License Number'
                            {...register(`rep${repIndex}DriverLicenseNumber`)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Driver License State'
                            {...register(`rep${repIndex}DriverLicenseState`)}
                        />
                    </Grid>
                    <Grid item sm={12}>
                        <TextField
                            variant="outlined"
                            size='small'
                            fullWidth
                            label='Email'
                            {...register(`rep${repIndex}Email`, { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })}
                            error={!!errors[`rep${repIndex}Email`]}
                            helperText={errors[`rep${repIndex}Email`]?.message}
                        />
                    </Grid>
                </Grid>
            </Fragment>
        )
    }

    return (
        <><Drawer
            open={open}
            onClose={onClose}
            title={t.VERIFICATION_REQUEST}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                    <div className='posting-container'>
                        <div className='heading-style-2 font-weight-bold mb-3'>{t.VERIFICATION_REQUEST}</div>

                        <div className='mb-3'>
                            <Controller
                                name="requestExactMatch"
                                control={methods.control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Request Exact Match for Global Watchlists"
                                    />
                                )}
                            />
                        </div>

                        {isDemoMode && (
                            <div className='mb-3'>
                                <div className='heading-style-2 mb-2'>Demo response</div>
                                <div className='d-flex flex-wrap' style={{ gap: 8 }}>
                                    {DEMO_SCENARIOS.map(scenario => (
                                        <Button
                                            key={scenario.value}
                                            size='small'
                                            variant={demoScenario === scenario.value ? 'contained' : 'outlined'}
                                            onClick={() => setDemoScenario(scenario.value)}
                                        >
                                            {scenario.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {verificationData.Company && (
                            <Fragment>
                                <div className='heading-style-2 mt-4 mb-2'>Business Information</div>
                                <hr className='mb-3' />
                                <Grid container spacing={2}>
                                    <Grid item sm={6}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Company Name'
                                            {...register('companyName', { required: 'Company Name is required' })}
                                            error={!!errors.companyName}
                                            helperText={errors.companyName?.message}
                                        />
                                    </Grid>
                                    <Grid item sm={6}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Alternate Company Name'
                                            {...register('alternateCompanyName')}
                                        />
                                    </Grid>
                                    <Grid item sm={12}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Street Address 1'
                                            {...register('companyStreetAddress1')}
                                        />
                                    </Grid>
                                    <Grid item sm={12}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Street Address 2'
                                            {...register('companyStreetAddress2')}
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='City'
                                            {...register('companyCity')}
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='State'
                                            {...register('companyState')}
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Postal Code'
                                            {...register('companyPostalCode')}
                                        />
                                    </Grid>
                                    <Grid item sm={6}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='Phone'
                                            {...register('companyPhone')}
                                        />
                                    </Grid>
                                    <Grid item sm={6}>
                                        <TextField
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            label='FEIN'
                                            {...register('companyFein', {
                                                validate: value => !value || /^\d{9}$/.test(value.replace(/-/g, '')) || 'EIN must be 9 digits'
                                            })}
                                            error={!!errors.companyFein}
                                            helperText={errors.companyFein?.message}
                                        />
                                    </Grid>
                                </Grid>
                            </Fragment>
                        )}

                        <div className='mt-4 mb-3'>
                            <div className='d-flex align-items-center justify-content-between'>
                                <h3 className='mb-0'>Authorized Representatives</h3>
                                {activeReps.length < 5 && (
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        onClick={addRepresentative}
                                        startIcon={<AddIcon />}
                                    >
                                        Add Rep
                                    </Button>
                                )}
                            </div>
                        </div>

                        {activeReps.map(repIndex => renderAuthorizedRepresentative(repIndex))}
                    </div>

                    <DrawerAction>
                        <Button
                            variant='text'
                            onClick={onClose}
                        >
                            {strings.CANCEL}
                        </Button>
                        <Button
                            type="submit"
                            loading={loading}
                        >
                            {strings.SUBMIT}
                        </Button>
                    </DrawerAction>
                </form>
            </FormProvider>
        </Drawer>

            <Dialog open={confirmDialogOpen} onClose={handleCancelSubmit}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    Are you sure you want to perform a new verification?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelSubmit} variant='text'>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmSubmit} variant='contained' color='primary'>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default VerificationPreview;